import React, { useState } from 'react';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    'bot-field': '' // Honeypot for spam prevention
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear submit error when user edits
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setSubmitError('');
    }
  };

  const validate = () => {
    let tempErrors = { name: '', email: '', message: '' };
    if (formData.name.trim().length < 2) tempErrors.name = 'Name must be at least 2 characters.';
    if (!/\S+@\S+\.\S+/.test(formData.email)) tempErrors.email = 'Please enter a valid email address.';
    if (formData.message.trim().length < 10) tempErrors.message = 'Message must be at least 10 characters.';
    setErrors(tempErrors);
    return Object.values(tempErrors).every(x => x === '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reject if honeypot was filled (spam)
    if (formData['bot-field']) {
      setSubmitStatus('success'); // Pretend success to not tip off bots
      return;
    }

    if (!validate()) return;

    setSubmitStatus('submitting');
    setSubmitError('');

    try {
      const payload = new URLSearchParams({
        'form-name': 'contact',
        name: formData.name,
        email: formData.email,
        message: formData.message
      });

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '', 'bot-field': '' });
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      setSubmitStatus('error');
      setSubmitError('Something went wrong. Please try again or contact us directly.');
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <h2>Contact Us</h2>
        <p className="contact-intro">
          Book a consultation with our Clinical Exercise Physiologists. We serve partners, CEOs, and professionals across London, Wimbledon, Fulham, Kensington, Chiswick, Surrey, and Sussex.
        </p>
        <p className="contact-disclaimer">
          This form is for general enquiries. It is not a substitute for professional medical advice. Please consult a healthcare provider for clinical questions.
        </p>
        <form name="contact" onSubmit={handleSubmit} data-netlify="true">
          <input type="hidden" name="form-name" value="contact" />
          {/* Honeypot: hidden from users, bots will fill it */}
          <p style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }} aria-hidden="true">
            <label>
              Don't fill this out: <input name="bot-field" value={formData['bot-field']} onChange={handleChange} tabIndex={-1} autoComplete="off" />
            </label>
          </p>

          {submitStatus === 'success' && (
            <div className="form-message form-message--success" role="status">
              Thank you. We'll be in touch soon.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="form-message form-message--error" role="alert">
              {submitError}
            </div>
          )}

          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={handleChange}
            required
            aria-describedby="name-error"
            disabled={submitStatus === 'submitting'}
          />
          {errors.name && <span id="name-error" className="error" role="alert">{errors.name}</span>}

          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleChange}
            required
            aria-describedby="email-error"
            disabled={submitStatus === 'submitting'}
          />
          {errors.email && <span id="email-error" className="error" role="alert">{errors.email}</span>}

          <label htmlFor="message">Message</label>
          <textarea
            id="message"
            name="message"
            placeholder="Your Message"
            value={formData.message}
            onChange={handleChange}
            required
            aria-describedby="message-error"
            disabled={submitStatus === 'submitting'}
          />
          {errors.message && <span id="message-error" className="error" role="alert">{errors.message}</span>}

          <button type="submit" disabled={submitStatus === 'submitting'}>
            {submitStatus === 'submitting' ? 'Sending…' : 'Send Message'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
